import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../utils/constants';
import { usePlanTier } from '../context/UserContext';
import { colors } from '../theme';
import { MarketRentAssumption } from '../utils/floorPlans';
import RentRollReview from './RentRollReview';
import {
  DEFAULT_DECISIONS,
  GrowthRate,
  ImportDecisions,
  ImportedUnit,
  RentRollParseResponse,
  materializeRentRoll,
} from '../utils/rentRollImport';

import { ConstructionLoader } from './ConstructionLoader';
interface RentRollImportProps {
  units: ImportedUnit[];
  setUnits: React.Dispatch<React.SetStateAction<any[]>>;
  marketRentAssumptions: MarketRentAssumption[];
  setMarketRentAssumptions: React.Dispatch<React.SetStateAction<MarketRentAssumption[]>>;
  growthRates: GrowthRate[];
  setGrowthRates: React.Dispatch<React.SetStateAction<GrowthRate[]>>;
  modelDetails: any;
}

/** Snapshot of everything an import overwrites, so Undo can put it all back. */
interface UndoSnapshot {
  units: any[];
  marketRentAssumptions: MarketRentAssumption[];
  growthRates: GrowthRate[];
  importedCount: number;
}

let importIdCounter = 0;
const makeUnitId = () => {
  importIdCounter += 1;
  return `${Date.now()}-import-${importIdCounter}`;
};

const ACCEPTED = '.xlsx,.xlsm,.csv,.pdf';

const RentRollImport: React.FC<RentRollImportProps> = ({
  units,
  setUnits,
  marketRentAssumptions,
  setMarketRentAssumptions,
  growthRates,
  setGrowthRates,
  modelDetails,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const planTier = usePlanTier();
  // Pro, matching the Excel export gate. "Pro or better" in this codebase means
  // everything except freemium — see isLocked() in ModelIntroSteps.
  const hasAccess = planTier !== 'freemium';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<RentRollParseResponse | null>(null);
  const [decisions, setDecisions] = useState<ImportDecisions>(DEFAULT_DECISIONS);
  const [undoSnapshot, setUndoSnapshot] = useState<UndoSnapshot | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const modelStartDate = modelDetails?.user_model_field_values?.find(
    (f: any) => f?.field_key === 'Model Start Date'
  )?.value;

  const upload = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const token = await getAccessTokenSilently();
      const form = new FormData();
      form.append('file', file);

      const res = await fetch(`${BACKEND_URL}/api/rent_roll/parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: form,
      });

      // The endpoint already phrases its rejections for a human — surface them
      // as-is rather than inventing a second set of wordings.
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        if (res.status === 422 && body?.error === 'unrecognized_format') {
          // A specific message means the server knows exactly what went wrong
          // — most often the daily AI cap. Showing the generic "unrecognised
          // layout" text there would send the user off debugging a fine file.
          setError(
            body?.message ||
            "We couldn't recognize the rent roll from this file. Support for new rent roll " +
            "formats is on the way. For now, request a standard excel export of the rent " +
            "roll from the seller. Please also send the PDF file you were trying to upload to " +
            "cdurkin@underwritre.com so we can work on fixing this issue."
          );
        } else {
          setError(body?.error || `Upload failed (${res.status}).`);
        }
        return;
      }
      setResponse(body as RentRollParseResponse);
      setDecisions(DEFAULT_DECISIONS);
    } catch (e) {
      setError('We could not reach the server. Check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || !files.length) return;
    upload(files[0]);
  };

  const handleImport = () => {
    if (!response) return;
    const result = materializeRentRoll({
      response,
      decisions,
      growthRates,
      modelStartDate,
      makeId: makeUnitId,
    });

    setUndoSnapshot({
      units: units.slice(),
      marketRentAssumptions: marketRentAssumptions.slice(),
      growthRates: growthRates.slice(),
      importedCount: result.units.length,
    });

    setGrowthRates(result.growthRates);
    setMarketRentAssumptions(result.marketRentAssumptions);
    setUnits(result.units);
    setResponse(null);
  };

  const handleUndo = () => {
    if (!undoSnapshot) return;
    setUnits(undoSnapshot.units);
    setMarketRentAssumptions(undoSnapshot.marketRentAssumptions);
    setGrowthRates(undoSnapshot.growthRates);
    setUndoSnapshot(null);
  };

  // ── Tier gate ────────────────────────────────────────────────────────────
  if (!hasAccess) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, textAlign: 'center' }}>
        <UploadFileIcon sx={{ fontSize: 48, color: colors.grey[400] }} />
        <Typography sx={{ fontWeight: 700, mt: 1, color: colors.grey[900] }}>
          Import a rent roll
        </Typography>
        <Typography variant="body2" sx={{ color: colors.grey[600], mt: 0.5, mb: 2 }}>
          Upload the rent roll export from your property management system and we'll build the
          unit mix, square footage and in-place rents for you.
        </Typography>
        <Button variant="contained" onClick={() => setUpgradeOpen(true)}>
          Available on the Pro plan
        </Button>

        <Snackbar
          open={upgradeOpen}
          autoHideDuration={6000}
          onClose={() => setUpgradeOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setUpgradeOpen(false)}
            severity="info"
            variant="filled"
            sx={{ width: '100%', alignItems: 'center', borderRadius: 2, px: 3, py: 1, boxShadow: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/settings?upgrade=pro')}
                sx={{ ml: 2, fontWeight: 700, textTransform: 'none' }}
              >
                Upgrade
              </Button>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
              Available on the Pro plan
            </Typography>
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ── Review ───────────────────────────────────────────────────────────────
  if (response) {
    return (
      <RentRollReview
        response={response}
        decisions={decisions}
        onDecisionsChange={setDecisions}
        onImport={handleImport}
        onStartOver={() => setResponse(null)}
      />
    );
  }

  // ── Upload ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {undoSnapshot && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleUndo}>
              Undo import
            </Button>
          }
        >
          Imported {undoSnapshot.importedCount.toLocaleString()} units. Switch to
          "Unit by unit" to review them.
        </Alert>
      )}

      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: `2px dashed ${dragActive ? colors.blue : colors.grey[300]}`,
          bgcolor: dragActive ? colors.blueTint : 'transparent',
          borderRadius: 2,
          p: { xs: 3, md: 6 },
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          transition: 'background-color 120ms ease, border-color 120ms ease',
        }}
      >
        {uploading ? (
          <>
            <ConstructionLoader size={48} label="Reading your rent roll" />
            <Typography sx={{ mt: 2, color: colors.grey[700] }}>
              Reading your rent roll…
            </Typography>
          </>
        ) : (
          <>
            <UploadFileIcon sx={{ fontSize: 48, color: colors.grey[400] }} />
            <Typography sx={{ fontWeight: 700, mt: 1, color: colors.grey[900] }}>
              Drop your rent roll here, or click to choose a file
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[600], mt: 0.5 }}>
              Excel (.xlsx, .xlsm), CSV or PDF up to 10 MB. A PDF needs selectable text — a
              scan can't be read. Tenant names are never stored.
            </Typography>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            // Allow re-picking the same file after an error.
            e.target.value = '';
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default RentRollImport;
